import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Send, Calendar, Clock } from 'lucide-react';
import {
  FaInstagram,
  FaLinkedinIn,
  FaRegEnvelope,
  FaXTwitter,
  FaYoutube
} from 'react-icons/fa6';

const ContactSection = () => {
  const contactInfo = [
    {
      icon: <FaRegEnvelope className='w-5 h-5' />,
      label: 'Email',
      value: 'hello@yomiakande.com',
      href: 'mailto:hello@yomiakande.com'
    }
  ];

  const socialLinks = [
    {
      icon: <FaLinkedinIn className='w-5 h-5' />,
      label: 'Yomi Akande LinkedIn Profile',
      href: 'https://www.linkedin.com/in/yomiakande/'
    },
    {
      icon: <FaXTwitter className='w-5 h-5' />,
      label: 'Yomi Akande X Profile',
      href: 'https://x.com/IamYomiAkande'
    },
    {
      icon: <FaInstagram className='w-5 h-5' />,
      label: 'Yomi Akande Instagram Profile',
      href: 'https://www.instagram.com/IamYomiAkande/'
    },
    {
      icon: <FaYoutube className='w-5 h-5' />,
      label: 'Yomi Akande YouTube Channel',
      href: '#'
    }
  ];

  return (
    <section id='contact' className='py-20 lg:py-32 bg-muted/30'>
      <div className='container mx-auto px-4 lg:px-8'>
        <div className='text-center mb-16'>
          <Badge variant='outline' className='mb-4'>
            Contact
          </Badge>
          <h2 className='text-3xl lg:text-5xl font-bold mb-6'>
            Let's <span className='gradient-text'>Connect</span>
          </h2>
          <p className='text-lg text-muted-foreground max-w-3xl mx-auto'>
            If you're building fintech infrastructure or scaling engineering teams, 
            I'm open to conversations. Let's connect to discuss how we can accelerate 
            your technology objectives.
          </p>
        </div>

        <div className='grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto'>
          {/* Contact Form */}
          <Card className='card-elegant'>
            <CardHeader>
              <CardTitle className='flex items-center'>
                <Send className='w-5 h-5 mr-2 text-primary' />
                Send a Message
              </CardTitle>
            </CardHeader>
            <CardContent className='space-y-6'>
              <div className='grid md:grid-cols-2 gap-4'>
                <div>
                  <label className='text-sm font-medium mb-2 block'>
                    First Name
                  </label>
                  <Input placeholder='Your first name' />
                </div>
                <div>
                  <label className='text-sm font-medium mb-2 block'>
                    Last Name
                  </label>
                  <Input placeholder='Your last name' />
                </div>
              </div>

              <div>
                <label className='text-sm font-medium mb-2 block'>Email</label>
                <Input type='email' placeholder='your.email@company.com' />
              </div>

              <div>
                <label className='text-sm font-medium mb-2 block'>
                  Company
                </label>
                <Input placeholder='Your company name' />
              </div>

              <div>
                <label className='text-sm font-medium mb-2 block'>
                  Subject
                </label>
                <Input placeholder='What would you like to discuss?' />
              </div>

              <div>
                <label className='text-sm font-medium mb-2 block'>
                  Message
                </label>
                <Textarea
                  placeholder='Tell me about your project, challenges, or how I can help...'
                  rows={5}
                />
              </div>

              <Button className='w-full' size='lg'>
                <Send className='w-4 h-4 mr-2' />
                Send Message
              </Button>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <div className='space-y-8'>
            {/* Contact Details */}
            <Card className='card-elegant'>
              <CardHeader>
                <CardTitle>Get in Touch</CardTitle>
              </CardHeader>
              <CardContent className='space-y-6'>
                {contactInfo.map((info) => (
                  <div key={info.label} className='flex items-center space-x-4'>
                    <div className='bg-primary/10 p-3 rounded-lg text-primary'>
                      {info.icon}
                    </div>
                    <div>
                      <p className='text-sm text-muted-foreground'>
                        {info.label}
                      </p>
                      {info.href ? (
                        <a
                          href={info.href}
                          className='font-medium hover:text-primary transition-colors'
                        >
                          {info.value}
                        </a>
                      ) : (
                        <p className='font-medium'>{info.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Schedule Meeting */}
            <Card className='card-elegant'>
              <CardHeader>
                <CardTitle className='flex items-center'>
                  <Calendar className='w-5 h-5 mr-2 text-primary' />
                  Schedule a Meeting
                </CardTitle>
              </CardHeader>
              <CardContent className='space-y-4'>
                <p className='text-muted-foreground'>
                  Prefer a direct conversation? Book a consultation call to
                  discuss your project in detail.
                </p>
                <div className='flex items-center text-sm text-muted-foreground mb-4'>
                  <Clock className='w-4 h-4 mr-2' />
                  Available Monday - Friday, 9:00 AM - 6:00 PM (WAT)
                </div>
                <Button variant='outline' className='w-full' size='lg'>
                  <Calendar className='w-4 h-4 mr-2' />
                  Book Consultation
                </Button>
              </CardContent>
            </Card>

            {/* Social Links */}
            <Card className='card-elegant'>
              <CardHeader>
                <CardTitle>Connect on Social</CardTitle>
              </CardHeader>
              <CardContent>
                <div className='flex space-x-4'>
                  {socialLinks.map((social) => (
                    <a
                      key={social.label}
                      href={social.href}
                      target='_blank'
                      className='bg-primary/10 p-3 rounded-lg text-primary hover:bg-primary hover:text-primary-foreground transition-colors'
                      aria-label={social.label}
                    >
                      {social.icon}
                    </a>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Response Time */}
            {/* <div className='bg-accent/10 p-6 rounded-lg border border-accent/20'>
              <h4 className='font-semibold text-accent mb-2'>
                Quick Response Guarantee
              </h4>
              <p className='text-sm text-muted-foreground'>
                I typically respond to all inquiries within 24 hours. For urgent
                matters, please call directly or mention "URGENT" in your
                subject line.
              </p>
            </div> */}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
